import React, { useEffect } from 'react';
import {
  ChartCard,
  HorizontalBars,
  InfoTooltip,
  type RelativePerformance,
  getRelativePerformance,
} from 'ui-v2';

import { type FragmentType, gql, useFragment } from '@/generated';
import { useChartTranslations } from '@/hooks/useChartTranslations';
import { useSortedChartData } from '../-hooks/useSortedChartData';
import { OptionalKey } from '../route';

export const KPIImpact_LandRestoredFragment = gql(/* GraphQL */ `
  fragment KPIImpact_LandRestoredFragment on ForestryIndicators {
    pctLandRestored {
      trimmedAvg
      isDataSuppressed
    }
  }
`);

type LandRestoredProps = {
  className?: string;
  indicators: FragmentType<typeof KPIImpact_LandRestoredFragment>;
  viewerIndicators?: FragmentType<typeof KPIImpact_LandRestoredFragment>;
  investmentLevel: OptionalKey;
  onEvaluatePerformance: (key: string, performance: RelativePerformance) => void;
};

export function LandRestored({
  indicators,
  viewerIndicators,
  investmentLevel,
  className,
  onEvaluatePerformance,
}: LandRestoredProps) {
  const benchmarkData = useFragment(KPIImpact_LandRestoredFragment, indicators);
  const viewerData = useFragment(KPIImpact_LandRestoredFragment, viewerIndicators);

  const benchmarkDataMap = {
    company: benchmarkData.pctLandRestored,
    investment: benchmarkData.pctLandRestored,
    rate_of_change: benchmarkData.pctLandRestored,
  };

  const viewerDataMap = {
    company: viewerData?.pctLandRestored,
    investment: viewerData?.pctLandRestored,
    rate_of_change: viewerData?.pctLandRestored,
  };

  const selectedBenchmarkData = benchmarkDataMap[investmentLevel];
  const selectedViewerData = viewerDataMap[investmentLevel];

  const { t, title, titleTooltipKPI } = useChartTranslations('landRestored');
  const getDisplayValue = (value?: number | null): string => {
    if (value == null) return '';
    const strValue = (value * 100).toLocaleString('en', { maximumFractionDigits: 0 });
    return t('displayValue', { value: strValue }) ?? strValue;
  };

  const { sortedChartData } = useSortedChartData({
    selectedBenchmarkData,
    selectedViewerData,
    getDisplayValue,
  });

  const relativePerformance = getRelativePerformance(
    selectedBenchmarkData?.trimmedAvg,
    selectedViewerData?.trimmedAvg
  );

  useEffect(() => {
    onEvaluatePerformance('landRestored', relativePerformance);
  }, [onEvaluatePerformance, relativePerformance]);

  return (
    <ChartCard className={className}>
      <ChartCard.Header className="flex items-center justify-between">
        <ChartCard.Title>
          {title}
          {titleTooltipKPI && <InfoTooltip className="ml-1">{titleTooltipKPI}</InfoTooltip>}
        </ChartCard.Title>
        {/* <Button variant="outline" className="shrink-0 font-semibold uppercase">
          view details
        </Button> */}
      </ChartCard.Header>
      <ChartCard.Body isDataSuppressed={selectedBenchmarkData.isDataSuppressed}>
        <HorizontalBars min={0} max={1} showInlineDisplayValue={true}>
          {sortedChartData.map((data, index) => (
            <HorizontalBars.Item
              key={index}
              label={data.label}
              value={data.value}
              displayValue={data.displayValue}
              color={data.color}
              isDashed={data.isDashed}
            />
          ))}
        </HorizontalBars>

        {/* <Link href="/impact-kpis/land-restored" className="mt-5 lg:hidden">
          <Button variant="outline" size="small" className="shrink-0 font-semibold uppercase">
            view details
          </Button>
        </Link> */}
      </ChartCard.Body>
    </ChartCard>
  );
}
